import React, { useEffect, useState } from 'react';
import { ST, fmtBaht, useT, countryName, flagEmoji } from './Chrome';
import { SiteFooterV2 } from './Footers';
import { fmtTmu, fmtThbExact, TMU_FALLBACK_LIMITS } from './tmuUtil';

///////////////////////////////////////////////////////////////////////////////
// /r/:shortId — public, no-login payer checkout for a Top Me Up link. The
// payer pays through Omise's own hosted Payment Links+ page (card or Thai
// wallets/bank apps); this page only collects the amount, method and an
// email for the receipt, then redirects. See lib/routes/api/topup-pay.js.
//
// Fully translated (useT()) — the receiver can share the link with ?l=<code>
// so it opens in whichever language they pick, independent of the language
// they used to create it. useT()/readLang() already honour ?l= site-wide.
///////////////////////////////////////////////////////////////////////////////

// Substitute {name}/{amount}/etc. placeholders in a translated string
function fill(s, vars) {
  return String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
}

// 'thai-wallets' matches the method code Checkout.jsx already uses for the
// same Omise rail — no standalone PromptPay support, so the label stays
// generic rather than naming a specific method that may not be enabled.
function methods(t) {
  return [
    { code: 'card', label: t('tmu_card_label'), sub: t('tmu_card_sub'), logos: ['visa.png', 'master.png', 'jcb.png'] },
    { code: 'thai-wallets', label: t('tmu_thai_label'), sub: t('tmu_thai_sub'), logos: ['linepay.svg', 'truemoney.svg', 'kbank.png', 'scb.png'] },
  ];
}

function shortIdFromPath() {
  const m = /^\/r\/([A-Za-z]{3}-[0-9A-Fa-f]{6,8})$/.exec(window.location.pathname);
  return m ? m[1] : '';
}

function RecipientHeroPhoto({ url, name }) {
  if (url) return <img className="tmu-hero-photo" src={url} alt="" width={140} height={140} />;
  const initial = ((name || '?').trim()[0] || '?').toUpperCase();
  return <span className="tmu-hero-photo tmu-hero-initials">{initial}</span>;
}

const SUCCESS_STYLE = { background: 'rgba(46,204,113,.1)', color: '#27ae60', border: '1px solid rgba(46,204,113,.3)', borderRadius: 8, padding: '12px 14px' };

export default function TopupPayPage({ me, currency, onCurrency }) {
  const t = useT();
  const shortId = shortIdFromPath();
  const [request, setRequest] = useState(undefined); // undefined=loading, null=not found/inactive
  const [loadError, setLoadError] = useState(null);
  const [amount, setAmount] = useState(null);
  const [method, setMethod] = useState('card');
  const [email, setEmail] = useState((me && me.email) || '');
  const [quote, setQuote] = useState(null); // { fee, total }
  const [busy, setBusy] = useState(false);
  const [payError, setPayError] = useState(null);
  const [usdPresets, setUsdPresets] = useState(TMU_FALLBACK_LIMITS.USD.presets);

  const params = new URLSearchParams(window.location.search);
  const paymentId = params.get('paymentId');
  const [pollStatus, setPollStatus] = useState(params.get('paid') === '1' && paymentId ? 'checking' : null);

  useEffect(() => {
    fetch(`/api/topup-pay/${encodeURIComponent(shortId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        setRequest(d);
        // THB rounds the default to 10; USD requests keep whole-dollar steps
        setAmount(d.amountType === 'fixed' ? d.amount
          : d.currency === 'USD' ? Math.round((d.minAmount + d.maxAmount) / 2)
            : Math.round((d.minAmount + d.maxAmount) / 2 / 10) * 10);
      })
      .catch(async (r) => {
        let msg = null;
        try { msg = await r.text(); } catch (e) { /* keep default */ }
        setLoadError(msg && msg.includes('no longer active') ? t('tmu_inactive') : t('tmu_not_found'));
        setRequest(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortId]);

  // USD requests: preset amounts come from the server config when reachable
  useEffect(() => {
    if (!request || request.currency !== 'USD') return;
    fetch('/api/topup-requests/config')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const p = d && d.limits && d.limits.USD && d.limits.USD.presets;
        if (Array.isArray(p) && p.length) setUsdPresets(p);
      })
      .catch(() => {});
  }, [request]);

  // Live fee/total preview whenever amount or method changes. POST per the
  // quote contract (USD requests need chargeAmount/rate); falls back to the
  // older GET if the POST route isn't there.
  useEffect(() => {
    if (!request || !amount) return;
    let dead = false;
    if (request.currency === 'USD') setQuote(null); // never show a stale USD total next to a new amount
    const base = `/api/topup-pay/${encodeURIComponent(shortId)}/quote`;
    fetch(base, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, method }),
    })
      .then((r) => (r.status === 404 || r.status === 405
        ? fetch(`${base}?amount=${amount}&method=${method}`) : r))
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!dead && d) setQuote(d); })
      .catch(() => {});
    return () => { dead = true; };
  }, [request, shortId, amount, method]);

  // Poll payment status after redirect back from Omise
  useEffect(() => {
    if (pollStatus !== 'checking') return;
    let cancelled = false;
    let attempts = 0;
    const poll = () => {
      fetch(`/api/topup-pay/${encodeURIComponent(shortId)}/status?paymentId=${encodeURIComponent(paymentId)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (cancelled) return;
          if (!d) { setPollStatus('failed'); return; }
          if (d.status === 'fulfilled') { setPollStatus('success'); return; }
          if (d.status === 'failed') { setPollStatus('failed'); return; }
          attempts += 1;
          if (attempts >= 6) { setPollStatus('timeout'); return; }
          setTimeout(poll, 5000);
        })
        .catch(() => { if (!cancelled) setPollStatus('failed'); });
    };
    poll();
    return () => { cancelled = true; };
  }, [pollStatus, shortId, paymentId]);

  function pay(e) {
    e.preventDefault();
    if (!request || busy) return;
    setPayError(null);
    if (!/^\S+@\S+\.\S+$/.test(email)) { setPayError(t('tmu_email_invalid')); return; }
    setBusy(true);
    fetch(`/api/topup-pay/${encodeURIComponent(shortId)}/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payerEmail: email, method, amount: request.amountType === 'open' ? amount : undefined }),
    })
      .then(async (r) => {
        const d = await r.json().catch(() => null);
        if (!r.ok || !d || !d.payment_url) throw new Error((d && d.message) || 'Payment could not be started.');
        window.location.href = d.payment_url;
      })
      .catch((err) => { setPayError(err.message); setBusy(false); });
  }

  const isUsd = !!request && request.currency === 'USD';
  const cur = isUsd ? 'USD' : 'THB';
  let presets = [];
  if (request && request.amountType === 'open') {
    presets = isUsd
      ? [...new Set([request.minAmount, ...usdPresets.filter((v) => v > request.minAmount && v < request.maxAmount), request.maxAmount])].sort((a, b) => a - b)
      : [...new Set([
        request.minAmount,
        Math.round(((request.minAmount + request.maxAmount) / 2) / 10) * 10,
        request.maxAmount,
      ])];
  }

  const iso = request ? (request.receiverCountryIso || (isUsd ? '' : 'TH')) : '';
  const recipientLabel = iso && iso !== 'TH'
    ? fill(t('tmu_recipient_in'), { country: `${flagEmoji(iso)} ${countryName(iso)}` })
    : t('tmu_recipient_thailand');

  const firstName = request ? request.receiverName.split(' ')[0] : '';
  const METHODS = methods(t);

  return (
    <div className="page">
      {request && (
        <div className="tmu-hero">
          <RecipientHeroPhoto url={request.receiverPhotoUrl} name={request.receiverName} />
        </div>
      )}
      <div className="checkout-wrap">
        {request === undefined && <div className="cocard"><div className="modal-loading">{t('tmu_loading')}</div></div>}

        {request === null && (
          <div className="cocard">
            <div className="notice bad">{loadError || t('tmu_not_found')}</div>
          </div>
        )}

        {pollStatus && (
          <div className="cocard">
            {pollStatus === 'checking' && <div className="notice">{t('tmu_checking_payment')}</div>}
            {pollStatus === 'success' && <div className="notice" style={SUCCESS_STYLE}>{fill(t('tmu_payment_success'), { name: request ? request.receiverName : '' })}</div>}
            {pollStatus === 'failed' && <div className="notice bad">{t('tmu_payment_failed')}</div>}
            {pollStatus === 'timeout' && <div className="notice">{t('tmu_payment_timeout')}</div>}
          </div>
        )}

        {request && pollStatus !== 'success' && (
          <>
            <div className="cocard tmu-requester">
              <h2 className="tmu-req-title">{fill(t('tmu_asking_help'), { name: request.receiverName })}</h2>
              <p className="tmu-req-sub">{fill(t('tmu_help_body'), { firstName })}</p>
            </div>

            {request.note && (
              <div className="tmu-note-wrap">
                <div className="tmu-note-label">{fill(t('tmu_note_says'), { name: firstName })}</div>
                <div className="tmu-note">{request.note}</div>
              </div>
            )}

            <div className="cocard tmu-recipient">
              <div className="tmu-recipient-label">{recipientLabel}</div>
              <div className="tmu-recipient-phone">{request.receiverPhone}</div>
              {request.receiverPhoneVerified && (
                <div><span className="tag good"><span className="tag-ico">✓</span> {t('tmu_verified_number')}</span></div>
              )}
            </div>

            {request.amountType === 'open' && (
              <div className="cocard">
                <h3 className="coh">{t('tmu_choose_amount')}</h3>
                <div className="tmu-amounts">
                  {presets.map((v) => (
                    <button type="button" key={v} className={`chip ${amount === v ? 'on' : ''}`} onClick={() => setAmount(v)}>
                      {fmtTmu(v, cur)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isUsd ? (
              <div className="cocard">
                <div className="sumrow"><span>{fill(t('tmu_receives'), { firstName })}</span><b className="mono">{fmtTmu(amount || 0, 'USD', true)} USD</b></div>
                <div className="sumrow"><span>{t('tmu_processing_fee')}</span><b className="mono">{quote ? fmtTmu(quote.fee, 'USD', true) : '—'}</b></div>
                <div className="sumrow"><span>{t('tmu_total_usd')}</span><b className="mono">{quote ? fmtTmu(quote.total, 'USD', true) : '—'}</b></div>
                <div className="sumrow total">
                  <span>{quote && quote.chargeAmount != null
                    ? fill(t('tmu_charged_thb'), { amount: fmtThbExact(quote.chargeAmount), rate: Number(quote.rate).toFixed(2) })
                    : t('tmu_total_pay')}</span>
                  <b className="mono">{quote && quote.chargeAmount != null ? fmtThbExact(quote.chargeAmount) : '—'}</b>
                </div>
              </div>
            ) : (
              <div className="cocard">
                <div className="sumrow"><span>{fill(t('tmu_amount_to'), { firstName })}</span><b className="mono">{fmtBaht(amount || 0)}</b></div>
                <div className="sumrow"><span>{t('tmu_processing_fee')}</span><b className="mono">{quote ? fmtBaht(quote.fee) : '—'}</b></div>
                <div className="sumrow total"><span>{t('tmu_total_pay')}</span><b className="mono">{quote ? fmtBaht(quote.total) : '—'}</b></div>
              </div>
            )}

            <div className="cocard">
              <h3 className="coh">{t('tmu_payment_method')}</h3>
              <div className="methods">
                {METHODS.map((m) => (
                  <button
                    type="button"
                    key={m.code}
                    className={`method ${method === m.code ? 'on' : ''}`}
                    onClick={() => setMethod(m.code)}
                    aria-pressed={method === m.code}
                  >
                    {m.logos && (
                      <span className="mlogos">{m.logos.map((l) => <img key={l} src={`${ST}/images/payment-icons/${l}`} alt="" />)}</span>
                    )}
                    <span className="mname">{m.label}</span>
                    <span className="msub">{m.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="cocard">
              <h3 className="coh">{t('tmu_your_email')}</h3>
              <input
                className="coinput"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label={t('tmu_your_email')}
              />
              <div className="cohint">{t('tmu_email_hint')}</div>
              {payError && <div className="notice bad payerr">{payError}</div>}
            </div>

            <form onSubmit={pay}>
              <button type="submit" className="btn block colarge" disabled={busy || !amount}>
                {busy ? t('tmu_starting_payment')
                  : isUsd && quote && quote.chargeAmount != null ? fill(t('tmu_pay_amount'), { amount: fmtThbExact(quote.chargeAmount) })
                    : fill(t('tmu_send_amount'), { amount: fmtTmu(amount || 0, cur) })}
              </button>
            </form>
            <div className="tmu-trust">{t('tmu_trust')}</div>
          </>
        )}
      </div>
      <SiteFooterV2 />
    </div>
  );
}
